import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { RightOutlined } from '@ant-design/icons';
import McqSidePanel from '../../components/Mcq/McqSidePanel';
import McqCard from '../../components/Mcq/McqCard';
import ProgressFooter from '../../components/ProgressFooter/ProgressFooter';
import { useSelector, useDispatch } from 'react-redux';
import * as actions from '../../store/actions';
import { showModal } from '../../store/actions/AssessmentActions';
import { switchToNextSection, showLastSectionCompletionModal } from '../../Utils/sectionUtils';
import { TRIGGER_BY_USER } from '../../const';

import styles from './McqCanvas.scss';
import useQuestionDetailsUpdater from '../../hooks/useQuestionDetailsUpdater';

const responseDetails = {
  id: '',
  visited: false,
  answered: false,
  markedForReview: false,
  answer: [],
};

const isLastInSection = (currentMcqId, mcqIds, sections, currentSectionId) => {
  if (sections && sections.length > 0) {
    const section = sections.find(s => s.id === currentSectionId);
    if (section && section.mcqs) {
      return section.mcqs.findIndex(mcqId => mcqId === currentMcqId) === section.mcqs.length - 1;
    }
    return false;
  } else {
    return mcqIds.findIndex(mcq => mcq.id === currentMcqId) === mcqIds.length - 1;
  }
};

const McqCanvas = props => {
  const { initialMcqId } = props;
  const [currentMcqId, setCurrentMcqId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSidePanelOpen, setIsSidePanelOpenState] = useState(true);
  const { mcqIds, sections, currentSectionId, mcqsResponses, test_settings } = useSelector(
    state => state.assessment,
  );
  const { mcqsQuestions, currentMcq, questionDetails, progress } = useSelector(state => state.mcq);
  const { assessment_id } = useSelector(state => state.invite);
  const dispatch = useDispatch();
  const history = useHistory();

  // Use the custom hook to update question details
  useQuestionDetailsUpdater(
    mcqIds,
    sections,
    currentSectionId,
    questionDetails,
    progress,
    mcqsResponses,
  );

  const updateQuestionDetailsDispatch = (index, action, value) => {
    dispatch(actions.updateQuestionDetails({ index, action, value }));
  };

  const loadOrSetCurrentQuestion = useCallback(
    mcqId => {
      if (!mcqId) return;

      const existingQuestion = mcqsQuestions.find(x => x.id === mcqId);

      if (existingQuestion) {
        if (!currentMcq || currentMcq.id !== mcqId) {
          dispatch(actions.setCurrentMcq(existingQuestion));
        }
      } else {
        dispatch(actions.fetchMcqQuestion(mcqId));
        // Find the index for updating question details based on current section
        const currentMcqIds = getCurrentMcqIds();
        const index = currentMcqIds.findIndex(mcq => mcq.id === mcqId);
        if (index !== -1) {
          dispatch(actions.updateQuestionDetails({ index, action: 'visited' }));
        }
      }
    },
    [mcqsQuestions, currentMcq, dispatch, sections, currentSectionId, mcqIds],
  );

  const handleSidePanelClick = mcqId => {
    if (mcqId && assessment_id) {
      history.push(`/assessment/${assessment_id}/mcqs/${mcqId}`);
    }
  };

  const setSidePanelOpen = () => {
    setIsSidePanelOpenState(prevState => !prevState);
  };

  const nextAndPreviousButtonClick = (mcqId, action) => {
    const currentMcqIds = getCurrentMcqIds();
    const currentIndex = currentMcqIds.findIndex(mcq => mcq.id === mcqId);
    if (currentIndex === -1) return;

    let newIndex = currentIndex;
    switch (action) {
      case 'next':
        if (currentIndex < currentMcqIds.length - 1) {
          newIndex = currentIndex + 1;
        }
        break;
      case 'previous':
        if (currentIndex >= 1) {
          newIndex = currentIndex - 1;
        }
        break;
      default:
        break;
    }

    if (newIndex !== currentIndex && currentMcqIds && currentMcqIds[newIndex] && assessment_id) {
      const newMcqId = currentMcqIds[newIndex].id;
      history.push(`/assessment/${assessment_id}/mcqs/${newMcqId}`);
    }
  };

  const onSubmitSection = () => {
    switchToNextSection({
      sections,
      currentSectionId,
      history,
      assessmentId: assessment_id,
      endBy: TRIGGER_BY_USER,
    });
  };

  const updateAnswer = (index, action, value, onSuccess, onError) => {
    setIsSaving(true);
    let promise;
    const currentQuestionDetail = questionDetails[index];
    const wasAnswered = currentQuestionDetail.answered;
    const oldAnswer = currentQuestionDetail.answer;
    switch (action) {
      case 'radio':
        dispatch(actions.updateQuestionDetails({ index, action: 'answered', value: [value] }));
        const parsedValue = JSON.parse(value);
        promise = dispatch(
          actions.recordResponse(currentMcq.id, [
            {
              option_id: parsedValue.id,
              text: parsedValue.text,
              question_order: parsedValue.order,
            },
          ]),
        );
        break;
      case 'checkbox':
        if (value.length !== 0) {
          dispatch(actions.updateQuestionDetails({ index, action: 'answered', value }));
          promise = dispatch(
            actions.recordResponse(
              currentMcq.id,
              value.map(v => {
                const parsedValue = JSON.parse(v);
                return {
                  option_id: parsedValue.id,
                  text: parsedValue.text,
                  question_order: parsedValue.order,
                };
              }),
            ),
          );
        } else {
          setIsSaving(false);
          return;
        }
        break;
      default:
        setIsSaving(false);
        return;
    }

    promise
      .then(() => {
        if (onSuccess) onSuccess();
      })
      .catch(error => {
        if (onError) onError();
        // Revert the answer if save failed
        if (wasAnswered) {
          dispatch(actions.updateQuestionDetails({ index, action: 'answered', value: oldAnswer }));
        } else {
          dispatch(actions.updateQuestionDetails({ index, action: 'unAnswered' }));
        }
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const calculatedProgress = useMemo(() => {
    // Determine which MCQs to use for progress calculation
    let currentMcqIds = mcqIds;
    if (sections && sections.length > 0 && currentSectionId) {
      const currentSection = sections.find(section => section.id === currentSectionId);
      if (currentSection && currentSection.mcqs) {
        currentMcqIds = currentSection.mcqs.map(mcqId => ({ id: mcqId }));
      }
    }

    const progress = {
      total: currentMcqIds ? currentMcqIds.length : 0,
      answered: 0,
      marked: 0,
      skipped: 0,
    };
    if (questionDetails) {
      const currentIndex = currentMcqId
        ? currentMcqIds.findIndex(mcq => mcq.id === currentMcqId)
        : -1;
      questionDetails.forEach((questionDetail, index) => {
        if (questionDetail.visited && !questionDetail.answered && currentIndex !== index) {
          progress.skipped += 1;
        }
        if (questionDetail.visited && questionDetail.answered) {
          progress.answered += 1;
        }
        if (questionDetail.markedForReview) {
          progress.marked += 1;
        }
      });
    }
    return progress;
  }, [questionDetails, currentMcqId, mcqIds, sections, currentSectionId]);

  useEffect(() => {
    dispatch(actions.setProgress(calculatedProgress));
  }, [calculatedProgress, dispatch]);

  useEffect(() => {
    if (assessment_id) {
      dispatch(actions.getRecoredResponses(assessment_id));
    }
  }, [assessment_id, dispatch]);

  useEffect(() => {
    if (initialMcqId && mcqIds.length > 0) {
      const mcqExists = mcqIds.some(mcq => mcq.id === initialMcqId);
      if (mcqExists) {
        setCurrentMcqId(initialMcqId);
      }
    }
  }, [initialMcqId, mcqIds]);

  useEffect(() => {
    if (currentMcqId) {
      loadOrSetCurrentQuestion(currentMcqId);
    }
  }, [currentMcqId, mcqIds]);

  // Determine current MCQ IDs based on sections
  const getCurrentMcqIds = () => {
    if (sections && sections.length > 0 && currentSectionId) {
      const currentSection = sections.find(section => section.id === currentSectionId);
      if (currentSection && currentSection.mcqs) {
        return currentSection.mcqs.map(mcqId => ({ id: mcqId }));
      }
    }
    return mcqIds;
  };

  const currentMcqIds = getCurrentMcqIds();

  return (
    <>
      <div className={styles['l-mcq-canvas']}>
        <div>
          {isSidePanelOpen ? (
            sections && sections.length > 0 ? (
              sections.map(section => (
                <McqSidePanel
                  key={section.id}
                  sectionHeading={section.title}
                  questionDetails={questionDetails}
                  isSelected={currentSectionId === section.id}
                  handleSidePanelClick={handleSidePanelClick}
                  currentMcqId={currentMcqId}
                  currentMcqIds={section.mcqs ? section.mcqs.map(mcqId => ({ id: mcqId })) : mcqIds}
                  isSaving={isSaving}
                  setSidePanelOpen={setSidePanelOpen}
                  sectionId={section.id}
                />
              ))
            ) : (
              <McqSidePanel
                sectionHeading="Questions"
                questionDetails={questionDetails}
                isSelected={true}
                handleSidePanelClick={handleSidePanelClick}
                currentMcqId={currentMcqId}
                currentMcqIds={mcqIds}
                isSaving={isSaving}
                setSidePanelOpen={setSidePanelOpen}
              />
            )
          ) : (
            <div
              className={styles['side-panel-mini']}
              onClick={setSidePanelOpen}
            >
              <RightOutlined />
            </div>
          )}
        </div>
        {currentMcqIds.length > 0 && currentMcqId && (
          <McqCard
            currentMcqId={currentMcqId}
            currentMcqIds={currentMcqIds}
            updateQuestiondetails={updateQuestionDetailsDispatch}
            nextAndPreviousButtonClick={nextAndPreviousButtonClick}
            updateAnswer={updateAnswer}
            isSaving={isSaving}
            isLastInSection={isLastInSection(currentMcqId, mcqIds, sections, currentSectionId)}
            isSidePanelOpen={isSidePanelOpen}
          />
        )}
      </div>
      <ProgressFooter
        onSubmitSection={onSubmitSection}
        isSaving={isSaving}
      />
    </>
  );
};

export default McqCanvas;
